import {
  AppBar,
  Toolbar,
  Stack,
  Button,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Avatar,
  Box,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../features/authSlice";

const NAV_BTN = {
  color: "rgba(255,255,255,0.82)",
  fontSize: 14,
  fontWeight: 500,
  textTransform: "none",
  px: 1.5,
  borderRadius: "8px",
  transition: "all 0.18s",
  "&:hover": { color: "#D4AF37", bgcolor: "rgba(212,175,55,0.12)" },
};

const DRAWER_BTN = {
  px: 3,
  py: 1.5,
  color: "rgba(255,255,255,0.78)",
  "&:hover": { bgcolor: "rgba(212,175,55,0.15)", color: "#D4AF37" },
};

const NavLink = ({ to, children }) => (
  <Link to={to} style={{ textDecoration: "none" }}>
    <Button sx={NAV_BTN}>{children}</Button>
  </Link>
);

const DrawerLink = ({ to, label, onClick }) => (
  <ListItem disablePadding>
    <ListItemButton component={Link} to={to} onClick={onClick} sx={DRAWER_BTN}>
      <ListItemText primary={label} primaryTypographyProps={{ fontSize: 15 }} />
    </ListItemButton>
  </ListItem>
);

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const userId = useSelector((state) => state.auth.user?._id);
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const handleLogOut = () => {
    localStorage.removeItem("currentUser");
    localStorage.setItem("isAuthenticated", "false");
    navigate("/login");
    dispatch(logout());
    setDrawerOpen(false);
  };

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase()
    : "";

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: "linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)",
        borderBottom: "1px solid rgba(212,175,55,0.18)",
        height: 65,
        justifyContent: "center",
      }}
    >
      <Toolbar sx={{ px: { xs: 2, md: 4 } }}>
        {/* Logo */}
        <Link to="/" style={{ textDecoration: "none" }}>
          <Typography
            sx={{
              fontSize: 22,
              fontWeight: 800,
              background: "linear-gradient(90deg, #fff 0%, #D4AF37 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.5px",
              userSelect: "none",
            }}
          >
            HasanPulse
          </Typography>
        </Link>

        <Box sx={{ flexGrow: 1 }} />

        {/* Desktop nav */}
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          <NavLink to="/">Home</NavLink>

          {isAuthenticated ? (
            <>
              <NavLink to={`/profile/${userId}`}>Profile</NavLink>
              <Button sx={NAV_BTN} onClick={handleLogOut}>
                Log Out
              </Button>
              <Link to={`/profile/${userId}`} style={{ textDecoration: "none", marginLeft: 8 }}>
                <Avatar
                  src={user?.profilePicture}
                  sx={{
                    width: 36,
                    height: 36,
                    border: "2px solid #D4AF37",
                    bgcolor: "#D4AF37",
                    color: "#1A1A2E",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "transform 0.18s",
                    "&:hover": { transform: "scale(1.08)" },
                  }}
                >
                  {initials}
                </Avatar>
              </Link>
            </>
          ) : (
            <>
              <NavLink to="/register">Register</NavLink>
              <NavLink to="/login">Log In</NavLink>
            </>
          )}
        </Stack>

        {/* Mobile menu */}
        <IconButton
          sx={{ color: "white", display: { xs: "flex", md: "none" } }}
          onClick={toggleDrawer(true)}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        <Box
          sx={{
            width: 270,
            height: "100%",
            background: "linear-gradient(180deg, #1A1A2E 0%, #16213E 100%)",
            color: "white",
          }}
        >
          {/* Drawer user header */}
          {isAuthenticated ? (
            <Box sx={{ px: 3, pt: 4, pb: 3, display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                src={user?.profilePicture}
                sx={{
                  width: 46,
                  height: 46,
                  border: "2px solid #D4AF37",
                  bgcolor: "#D4AF37",
                  color: "#1A1A2E",
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                {initials}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: 12, mt: 0.3 }}>
                  @{user?.username}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box sx={{ px: 3, pt: 4, pb: 3 }}>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 800,
                  background: "linear-gradient(90deg, #fff 0%, #D4AF37 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                HasanPulse
              </Typography>
            </Box>
          )}

          <Divider sx={{ borderColor: "rgba(212,175,55,0.18)", mb: 1 }} />

          <List disablePadding>
            <DrawerLink to="/" label="Home" onClick={toggleDrawer(false)} />
            {isAuthenticated && (
              <DrawerLink to={`/profile/${userId}`} label="Profile" onClick={toggleDrawer(false)} />
            )}
            {!isAuthenticated && (
              <DrawerLink to="/register" label="Register" onClick={toggleDrawer(false)} />
            )}
            {!isAuthenticated && (
              <DrawerLink to="/login" label="Log In" onClick={toggleDrawer(false)} />
            )}
            {isAuthenticated && (
              <ListItem disablePadding>
                <ListItemButton onClick={handleLogOut} sx={DRAWER_BTN}>
                  <ListItemText
                    primary="Log Out"
                    primaryTypographyProps={{ fontSize: 15 }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
